import { readFile } from 'node:fs/promises';
import { homedir } from 'node:os';
import { join } from 'node:path';

import type { AntigravityAccounts, OpenCodeAuth, ProviderAuthData } from './types.ts';

export const xdgDataHome = (): string =>
  process.env.XDG_DATA_HOME ?? join(homedir(), '.local', 'share');

export const xdgConfigHome = (): string =>
  process.env.XDG_CONFIG_HOME ?? join(homedir(), '.config');

export const AUTH_PATHS = {
  opencode: (): string => join(xdgDataHome(), 'opencode', 'auth.json'),
  codexPlugin: (): string => join(homedir(), '.opencode', 'auth', 'openai.json'),
  antigravityConfig: (): string => join(xdgConfigHome(), 'opencode', 'antigravity-accounts.json'),
  antigravityData: (): string => join(xdgDataHome(), 'opencode', 'antigravity-accounts.json'),
} as const;

const readJson = async <T>(filePath: string): Promise<T | null> => {
  try {
    const content = await readFile(filePath, 'utf-8');
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
};

export const loadOpenCodeAuth = async (): Promise<OpenCodeAuth | null> => {
  return readJson<OpenCodeAuth>(AUTH_PATHS.opencode());
};

export const getCodexAuth = async (): Promise<ProviderAuthData | null> => {
  const globalAuth = await loadOpenCodeAuth();
  if (globalAuth) {
    const openaiAuth = globalAuth.openai;
    if (openaiAuth && typeof openaiAuth === 'object' && (openaiAuth.access || openaiAuth.token)) {
      return openaiAuth as ProviderAuthData;
    }
  }

  const codexAuth = await readJson<ProviderAuthData>(AUTH_PATHS.codexPlugin());
  if (codexAuth?.access || codexAuth?.token) {
    return codexAuth;
  }

  return null;
};

export const getAntigravityAccounts = async (): Promise<AntigravityAccounts | null> => {
  const configAccounts = await readJson<AntigravityAccounts>(AUTH_PATHS.antigravityConfig());
  if (configAccounts?.accounts?.length) {
    return configAccounts;
  }

  const dataAccounts = await readJson<AntigravityAccounts>(AUTH_PATHS.antigravityData());
  if (dataAccounts?.accounts?.length) {
    return dataAccounts;
  }

  return null;
};

export const getZaiApiKey = async (): Promise<string | null> => {
  if (process.env.ZAI_API_KEY) {
    return process.env.ZAI_API_KEY;
  }

  const globalAuth = await loadOpenCodeAuth();
  if (!globalAuth) {
    return null;
  }

  const zaiAuth = globalAuth.zai ?? globalAuth['z.ai'];
  if (!zaiAuth) {
    return null;
  }

  if (typeof zaiAuth === 'string') {
    return zaiAuth;
  }

  if (typeof zaiAuth === 'object') {
    return zaiAuth.api_key ?? zaiAuth.token ?? null;
  }

  return null;
};
